// TESSERAX — REJEU (replay) : journal d'entrées déterministe. Cœur PUR, testé.
//
// Une partie est ENTIÈREMENT déterminée par (graine + journal d'entrées horodaté) :
// tout le hasard de jeu passe par `mulberry32(graine)` (séquence de pièces, missions,
// offres de reliques) et la gravité est basée sur le TEMPS écoulé (indépendante du
// frame-rate). Capturer ce journal permet donc de REJOUER une partie à l'identique —
// d'où le rejeu PARTAGEABLE (un lien qui montre le GESTE), puis le puits fantôme et
// l'anti-triche par re-simulation. Ce module ne porte QUE le FORMAT de données (codec
// + validation) ; la capture et la lecture (DOM / renderer) vivent dans `main.ts`.
//
// Décision de FIDÉLITÉ (cf. plan) : on capture la relique ACQUISE par son `id`, pas
// l'index de l'offre. L'offre (`draftOffer`) dépend de `meta.unlocked` — un état HORS
// (graine + entrées) — donc rejouer l'index divergerait pour un autre joueur (ou après
// un nouveau déblocage). L'`id` est stable et data-driven → rejeu fidèle partout. Le
// reroll n'a pas besoin d'être capturé (flux `relicRng` SÉPARÉ, ne touche pas la
// séquence de pièces) ; seuls le CHOIX (id) et le SAUT (skip, +1 charge → affecte
// l'issue) comptent.
//
// Pur : aucune dépendance THREE / DOM / localStorage / RNG. Le journal est une
// RELECTURE → n'altère ni le tirage ni le score (déterminisme du défi intact).

// Version du format : bumpée si le sens des codes change (les vieux liens dégradent
// gracieusement plutôt que de rejouer faux). v2 : ajout du Pli (CODE.FOLD), désormais signature de tous les modes.
export const REPLAY_VERSION = 2;

// Codes d'évènement. Les DÉCISIONS du joueur (0–16) sont en repère GRILLE (déjà résolues depuis
// le repère caméra par `move`) → rejeu fidèle quelle que soit l'orientation de la caméra.
// Les ÉVÈNEMENTS DE TIMING (17–19) sont des transitions DÉTERMINISTES mais pilotées par l'horloge
// (chute auto, verrouillage, fin de Zone chronométrée) : leur instant dépend du frame-rate du moteur
// vif (RAF ~60 ips), ABSENT du journal → non reconstructible depuis les seules entrées joueur. On les
// capture pour un rejeu HEADLESS BIT-EXACT (core/sim, → puits fantôme + anti-triche). La lecture M1 à
// travers le vrai moteur les IGNORE (le loop régénère sa propre gravité/lock).
export const CODE = {
  MOVE_XP: 0, MOVE_XN: 1, MOVE_ZP: 2, MOVE_ZN: 3, // glissement horizontal (±x, ±z)
  ROT_X: 4, ROT_Y: 5, ROT_Z: 6,                   // rotation autour d'un axe
  SOFT: 7, HARD: 8,                                // chute douce (1 cran) / dure
  HOLD: 9,                                         // réserve (C)
  ZONE: 10,                                        // activation du pouvoir Zone (F)
  BASC_PX: 11, BASC_NX: 12, BASC_PZ: 13, BASC_NZ: 14, // Bascule + mur visé
  RELIC: 15,        // arg = id de la relique acquise (string, ex. "obsidienne")
  RELIC_SKIP: 16,   // offre passée (+1 charge) — affecte l'issue, donc capturé
  GRAVITY: 17,      // un cran de chute AUTOMATIQUE (boucle d'anim) — timing
  LOCK: 18,         // verrouillage de la pièce (hard-drop OU expiration du lock-delay) — timing
  ZONE_END: 19,     // fin de la fenêtre Zone (chronométrée) — timing
  FOLD: 20,         // LE PLI (tous modes) : échange la tranche active (sans arg) — DÉCISION joueur
} as const;

// Évènements de TIMING : ignorés par la lecture M1 (le moteur vif régénère sa propre horloge),
// consommés par le rejeu headless bit-exact (core/sim).
export function isTimingCode(code: number): boolean {
  return code === CODE.GRAVITY || code === CODE.LOCK || code === CODE.ZONE_END;
}

// Codes connus (validation). Dérivé de CODE → croît automatiquement avec lui.
const KNOWN_CODES: ReadonlySet<number> = new Set(Object.values(CODE));

// Mur visé d'une Bascule, par code (utilitaire de lecture côté main.ts).
const BASC_DIR: Record<number, string> = {
  [CODE.BASC_PX]: 'px', [CODE.BASC_NX]: 'nx', [CODE.BASC_PZ]: 'pz', [CODE.BASC_NZ]: 'nz',
};
export function basculeDirOf(code: number): string | null { return BASC_DIR[code] || null; }
export function isBasculeCode(code: number): boolean { return code >= CODE.BASC_PX && code <= CODE.BASC_NZ; }

// Modes dont une partie est REJOUABLE (tous, depuis que chaque run est seedée). Le mode
// fixe le `levelOffset`/la gravité de base → fait partie de l'entête. `strates` (ascension
// roguelite) est lui aussi 100 % seedé — catalogue STRATA fixe + mutateurs dérivés de la
// graine — donc rejouable comme les autres ; l'oublier ici rendait le PARTAGE d'une run
// Strates muet (validateReplay refusait le mode → lien mort « ne fait rien »).
export const REPLAY_MODES: ReadonlySet<string> = new Set(['daily', 'free', 'zen', 'sprint', 'ultra', 'blitz', 'strates']);

// Garde-fous de taille : une longue partie endless ~ quelques milliers d'entrées. Le
// plafond protège le stockage / le décodage d'un payload hostile (lien partagé).
export const MAX_EVENTS = 100_000;
const MAX_RELIC_ID = /^[a-z_]{1,24}$/; // ids de reliques : data-driven, [a-z_]
// Noms réservés : un id de relique ne sera jamais l'un d'eux (le lookup catalogue
// échouerait sans risque), mais on les refuse par principe (garde anti-pollution de
// prototype, cf. posture du projet) car ils passent le motif [a-z_].
const RESERVED_KEYS: ReadonlySet<string> = new Set(['__proto__', 'prototype', 'constructor']);

// Un évènement : [tMs écoulé depuis le début, code, arg?]. `arg` n'existe que pour RELIC.
export type InputEvent = [number, number] | [number, number, string];

export interface Replay {
  v: number;        // version de format
  seed: number;     // graine mulberry32 de la run (entier 32 bits)
  mode: string;     // mode de jeu (cf. REPLAY_MODES)
  events: InputEvent[];
}

// ---- Encodage compact (texte délimité, delta-temps) ----
// Champs séparés par '~', évènements par ';', sous-champs par ','. tMs est stocké en
// DELTA base-36 (petits nombres) ; le code en base-36 ; l'arg (id de relique) en clair
// ([a-z_] → aucun conflit avec les délimiteurs). Compact ET trivialement correct.
export function encodeReplay(r: Replay): string {
  const head = [REPLAY_VERSION, (r.seed >>> 0).toString(36), String(r.mode)].join('~');
  let prev = 0;
  const evs = (r.events || []).map((e) => {
    const t = Math.max(0, Math.floor(e[0]) || 0);
    const dt = Math.max(0, t - prev); prev = t;
    let s = dt.toString(36) + ',' + ((e[1] | 0) >>> 0).toString(36);
    if (e.length > 2 && e[2] != null) s += ',' + String(e[2]);
    return s;
  }).join(';');
  return head + '~' + evs;
}

// ---- Décodage TOLÉRANT (entrée hostile / corrompue → null, jamais d'exception) ----
export function decodeReplay(str: string | null | undefined): Replay | null {
  if (typeof str !== 'string' || str.length === 0) return null;
  try {
    const sep = str.indexOf('~');
    if (sep < 0) return null;
    const parts = str.split('~');
    if (parts.length < 3) return null;
    const v = parseInt(parts[0], 10);
    const seed = parseInt(parts[1], 36) >>> 0;
    const mode = parts[2];
    const body = parts.slice(3).join('~'); // l'arg ne contient jamais '~' ; robuste quand même
    if (!Number.isFinite(v) || !Number.isFinite(seed)) return null;
    const events: InputEvent[] = [];
    let t = 0;
    if (body) {
      const toks = body.split(';');
      for (let i = 0; i < toks.length; i++) {
        const tok = toks[i];
        if (!tok) continue;
        const f = tok.split(',');
        const dt = parseInt(f[0], 36);
        const code = parseInt(f[1], 36);
        if (!Number.isFinite(dt) || dt < 0 || !Number.isFinite(code)) return null;
        t += dt;
        if (f.length > 2 && f[2]) events.push([t, code, f[2]]);
        else events.push([t, code]);
        if (events.length > MAX_EVENTS) return null;
      }
    }
    return { v, seed, mode, events };
  } catch {
    return null;
  }
}

// ---- Validation stricte (bornes ; codes connus ; temps croissant ; id de relique sain) ----
export function validateReplay(r: Replay | null | undefined): boolean {
  if (!r || typeof r !== 'object') return false;
  if (!Number.isFinite(r.seed)) return false;
  if (typeof r.mode !== 'string' || !REPLAY_MODES.has(r.mode)) return false;
  if (!Array.isArray(r.events)) return false;
  if (r.events.length > MAX_EVENTS) return false;
  let prev = 0;
  for (let i = 0; i < r.events.length; i++) {
    const e = r.events[i];
    if (!Array.isArray(e) || e.length < 2) return false;
    const t = e[0], code = e[1];
    if (!Number.isFinite(t) || t < prev) return false; // temps strictement croissant (≥)
    prev = t;
    if (!KNOWN_CODES.has(code as number)) return false;
    if (code === CODE.RELIC) {
      if (typeof e[2] !== 'string' || !MAX_RELIC_ID.test(e[2]) || RESERVED_KEYS.has(e[2])) return false;
    }
  }
  return true;
}

// Durée d'un rejeu (tMs du dernier évènement) — pour l'UI de lecture (barre de seek).
export function replayDurationMs(r: Replay | null | undefined): number {
  if (!r || !Array.isArray(r.events) || r.events.length === 0) return 0;
  return Math.max(0, r.events[r.events.length - 1][0] | 0);
}
